import type { ForwardRefExoticComponent, RefAttributes } from "react";
import { BarChart2, Loader, MessageSquare, CircleCheck, MessageCircle, Workflow, type LucideProps } from "lucide-react";

import StateIndicator, { type StateIndicatorVariantType } from "./stateIndicator";
import { CommercialHistoryItemLayoutTypes, CommercialHistoryItemStatusTypes } from "../types/commercialHistoryTypes";
import type { CommercialHistoryItemStatusType } from "../../../../chat/hooks/useCommercialHistoryItem";
import { StateIndicatorVariantTypes } from "../types/stateIndicatorTypes";

export type CommercialHistoryItemLayoutType = typeof CommercialHistoryItemLayoutTypes[keyof typeof CommercialHistoryItemLayoutTypes];

const statusConfig: Record<CommercialHistoryItemStatusType, {
    indicatorVariant: StateIndicatorVariantType;
    indicatorText: string;
    inlineIndicatorVariant: StateIndicatorVariantType;
    inlineIndicatorText: string;
    icon: ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>>;
    inlineIcon: ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>>;
    iconContainerClassName?: string;
}> = {
    [CommercialHistoryItemStatusTypes.SIMULATION_COMPLETE]: {
        indicatorVariant: StateIndicatorVariantTypes.SUCCESS,
        indicatorText: "Simulation Complete",
        inlineIndicatorVariant: StateIndicatorVariantTypes.SUCCESS,
        inlineIndicatorText: "Simulation · Complete",
        icon: BarChart2,
        inlineIcon: CircleCheck
    },
    [CommercialHistoryItemStatusTypes.SIMULATION_RUNNING]: {
        indicatorVariant: StateIndicatorVariantTypes.INFO,
        indicatorText: "Simulation Running",
        inlineIndicatorVariant: StateIndicatorVariantTypes.INFO,
        inlineIndicatorText: "Simulation · Running",
        icon: Loader,
        inlineIcon: CircleCheck,
        iconContainerClassName: "icon-rotate-infinite k-text-brand-info"
    },
    [CommercialHistoryItemStatusTypes.OPEN_CONVERSATION]: {
        indicatorVariant: StateIndicatorVariantTypes.NEUTRAL,
        indicatorText: "Open Conversation",
        inlineIndicatorVariant: StateIndicatorVariantTypes.WARNING,
        inlineIndicatorText: "Ask Sovra · Conversation",
        icon: MessageSquare,
        inlineIcon: MessageCircle,
        iconContainerClassName: "k-text-tertiary"
    },
    [CommercialHistoryItemStatusTypes.WORKFLOW]: {
        indicatorVariant: StateIndicatorVariantTypes.SUCCESS,
        indicatorText: "Workflow Complete",
        inlineIndicatorVariant: StateIndicatorVariantTypes.SUCCESS,
        inlineIndicatorText: "Workflow · Complete",
        icon: Workflow,
        inlineIcon: Workflow
    }
};

type CommercialHistoryItemCardProps = {
    title: string;
    contextLine: string;
    status: CommercialHistoryItemStatusType;
    metaText: string;
    isSelected: boolean;
    layout?: CommercialHistoryItemLayoutType;
    badgeHasIcon: boolean;
    onSelect: () => void;
};

const CommercialHistoryItemCard = ({
    title,
    contextLine,
    status,
    metaText,
    isSelected,
    badgeHasIcon,
    layout = CommercialHistoryItemLayoutTypes.DETAIL,
    onSelect
}: CommercialHistoryItemCardProps) => {
    const {
        indicatorVariant,
        inlineIndicatorVariant,
        indicatorText,
        inlineIndicatorText,
        icon: StatusIcon,
        inlineIcon: StatusInlineIcon,
        iconContainerClassName
    } = statusConfig[status];

    const itemClassName = `commercial-history-item rounded-3 min-w-0 ${isSelected ? "commercial-history-item--selected" : "commercial-history-item--default bg-action-tertiary-hover"}`;

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect();
        }
    };

    if (layout === CommercialHistoryItemLayoutTypes.INLINE) {
        return (
            <div
                className={itemClassName}
                role="button"
                tabIndex={0}
                onClick={onSelect}
                onKeyDown={handleKeyDown}
            >
                <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2 gap-sm-3 min-w-0 text-highlight-brand-hover">
                    <div className="min-w-0">
                        <p className="mb-0 fs-7">{title}</p>
                        <p className="mb-0 fs-8 k-text-subtle">{contextLine}</p>
                    </div>
                    <div className="d-flex align-items-center gap-3 flex-shrink-0">
                        <StateIndicator
                            variant={inlineIndicatorVariant}
                            text={inlineIndicatorText}
                            compact={true}
                            hasIcon={badgeHasIcon}
                            Icon={StatusInlineIcon}
                        />
                        {metaText && <span className="fs-8 k-text-subtle text-nowrap">{metaText}</span>}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={itemClassName}
            role="button"
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={handleKeyDown}
        >
            <div className="d-flex align-items-start gap-2 min-w-0">
                <span className={`d-inline-flex flex-shrink-0 ${iconContainerClassName}`}>
                    <StatusIcon className={isSelected ? "k-text-primary" : ""} size={16} strokeWidth={2} />
                </span>
                <div className="commercial-history-item-content flex-grow-1 min-w-0">
                    <p className="commercial-history-item-title fs-7 mb-0 fw-bold k-text-primary">{title}</p>
                    <p className="commercial-history-item-context mb-0">{contextLine}</p>
                    <div className="commercial-history-item-footer">
                        <div className="commercial-history-item-status">
                            <StateIndicator
                                variant={indicatorVariant}
                                text={indicatorText}
                                compact={true}
                                hasIcon={badgeHasIcon}
                            />
                        </div>
                        {metaText && (
                            <span className="commercial-history-item-meta">{metaText}</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CommercialHistoryItemCard;
